import {
	afterNextRender,
	Component,
	computed,
	DestroyRef,
	effect,
	inject,
	OnDestroy,
	signal,
	untracked,
	viewChild,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { PageEvent } from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { MsalService } from '@azure/msal-angular';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ENVIRONMENT } from '../../../../environments/environment.interface';
import { DatabaseViewAssignmentResponse, DatabaseViewSchema } from '../../../api/app-types/database-view.app';
import { CATEGORIES_API } from '../../../api/injection-tokens/services.token';
import { provideCategoryApi } from '../../../api/services/categories.service';
import { DATABASE_VIEW_API, provideDatabaseViewApi } from '../../../api/services/database-view.service';
import { DISABILITY_TYPE_SERVICE, provideDisabilityTypeApi } from '../../../api/services/disability-type.api';
import { INTEGRATION_OFFICE_SERVICE, provideIsoApi } from '../../../api/services/integration-office.service';
import { LEGAL_FOUNDATION_SERVICE, provideLegalFoundationApi } from '../../../api/services/legal-foundation.service';
import { provideUserApi, USER_SERVICE } from '../../../api/services/user.service';
import { LvrButton } from '../../../core/components/lvr-button/lvr-button';
import { Paginator } from '../../../core/components/paginator/paginator';
import { ERROR_PROVIDER } from '../../../core/services/error.service';
import { TENANT_FEATURES } from '../../../core/services/tenant-feature.service';
import { formatDateToISOString, matDateProviders, parseStoredDay } from '../../../shared/helpers/date.func';
import { NOTIFICATION_SERVICE } from '../../../shared/notification/notification';
import { createErrorNotification } from '../../../shared/notification/notification.factory';
import { LocalStorageService } from '../../../shared/storage/local-storage.service';
import { SessionManagerService } from '../../../shared/storage/session-manager.service';
import { REPRESENTATION_MODE } from '../../../core/services/representation-mode.service';
import { DatabaseColumnSelector } from '../shared/components/database-column-selector/database-column-selector';
import {
	DatabaseDateRangeFilter,
	DatabaseDateRangeValue,
} from '../shared/components/database-date-range-filter/database-date-range-filter';
import { DatabaseReferenceDateFilter } from '../shared/components/database-single-date-filter/database-reference-date-filter';
import { DatabaseFilterPanelRegistry } from '../shared/components/database-multi-select-filter/database-filter-panel.registry';
import { DatabaseMultiSelectFilter } from '../shared/components/database-multi-select-filter/database-multi-select-filter';
import { DatabaseColumnOption } from '../shared/models/database-column-selection.types';
import { DatabaseListColumn, DatabaseSortDirection } from '../shared/models/database-list.types';
import { DatabaseFilterOption } from '../shared/models/database-view-storage.types';
import { DatabaseDateFormatService } from '../shared/services/database-date-format.service';
import { DatabaseViewStorage } from '../shared/services/database-view-storage.service';
import {
	areColumnIdsEqual,
	reconcileColumnIds,
	resetStateForRemovedColumns,
} from '../shared/utils/database-column-selection';
import { runDatabaseCsvExport } from '../shared/utils/database-csv-export';
import { isReferenceDateAvailable, REFERENCE_DATE_COLUMN_IDS } from '../shared/utils/database-reference-date';
import { DATABASE_PHASE_FILTER_OPTIONS } from '../shared/utils/database-filter-options';
import {
	DATABASE_FILTER_SOURCE_KEYS,
	DatabaseFilterSourceKey,
	loadDatabaseFilterSourceOptions,
	nameOptions,
} from '../shared/utils/database-filter-sources';
import {
	createRecordFromKeys,
	filterOptionLabels,
	filterOptionValues,
	isNumericFilterId,
	toggleFilterSelection,
} from '../shared/utils/database-filter-state';
import {
	DatabaseRangePreset,
	isDatabaseRangePreset,
	resolveDatabaseRangePreset,
} from '../shared/utils/database-range-preset';

import {
	ASSIGNMENT_DATE_RANGE_FIELD_IDS,
	AssignmentDateRangeFieldId,
	AssignmentListFilterKey,
	AssignmentListSortField,
	buildDatabaseViewAssignmentRequest,
} from './database-assignments-list-request';

interface AssignmentRow {
	assignmentId: number;
	clientId: number;
	ifdId: number;
	advisorId: number;
	advisorObjectId?: string;
	clientName: string;
	phase: string;
	assignmentType: string;
	careType: string;
	initiatingOffice: string;
	caseGroup: string;
	counselingStart: string;
	assignmentStart: string;
	deregistrationDate: string;
	basis: string;
	assignmentNo: string;
	reportDue: string;
	ifd: string;
	service: string;
	advisor: string;
	sponsorGroup: string;
	sponsorCategory: string;
	disabilityType: string;
	institutionAtStart: string;
	institutionAtDate: string;
	institutionAtEnd: string;
	schoolNumberAtStart: string;
	schoolNumberAtDate: string;
	schoolNumberAtEnd: string;
	socialSecurityStatusAtStart: string;
	socialSecurityStatusAtDate: string;
	socialSecurityStatusAtEnd: string;
	disabilityTypeAtStart: string;
	disabilityTypeAtDate: string;
	disabilityTypeAtEnd: string;
	disabilityProofAtStart: string;
	disabilityProofAtDate: string;
	disabilityProofAtEnd: string;
	gdbAtStart: string;
	gdbAtDate: string;
	gdbAtEnd: string;
	lastThreeContactDates: string;
	contactTaskCount: number | '-';
	impedimentsAtStart: string;
	impedimentsAtEnd: string;
}

type FilterKey = AssignmentListFilterKey;
type ColumnKey =
	| 'clientName'
	| 'phase'
	| 'assignmentType'
	| 'careType'
	| 'initiatingOffice'
	| 'caseGroup'
	| 'counselingStart'
	| 'assignmentStart'
	| 'deregistrationDate'
	| 'reportDue'
	| 'basis'
	| 'assignmentNo'
	| 'ifd'
	| 'service'
	| 'advisor'
	| 'sponsorGroup'
	| 'sponsorCategory'
	| 'disabilityType'
	| 'institutionAtStart'
	| 'institutionAtDate'
	| 'institutionAtEnd'
	| 'schoolNumberAtStart'
	| 'schoolNumberAtDate'
	| 'schoolNumberAtEnd'
	| 'socialSecurityStatusAtStart'
	| 'socialSecurityStatusAtDate'
	| 'socialSecurityStatusAtEnd'
	| 'disabilityTypeAtStart'
	| 'disabilityTypeAtDate'
	| 'disabilityTypeAtEnd'
	| 'disabilityProofAtStart'
	| 'disabilityProofAtDate'
	| 'disabilityProofAtEnd'
	| 'gdbAtStart'
	| 'gdbAtDate'
	| 'gdbAtEnd'
	| 'lastThreeContactDates'
	| 'contactTaskCount'
	| 'impedimentsAtStart'
	| 'impedimentsAtEnd';

type AssignmentColumn = DatabaseListColumn<ColumnKey, AssignmentListSortField, FilterKey>;

const ASSIGNMENT_SCHEMA_IDS: Record<ColumnKey, string> = {
	clientName: 'client',
	phase: 'phase',
	assignmentType: 'assignment',
	careType: 'typeOfCare',
	initiatingOffice: 'initiator',
	caseGroup: 'caseGroup',
	counselingStart: 'startOfConsultation',
	assignmentStart: 'startOfCare',
	deregistrationDate: 'cancellationDate',
	reportDue: 'reportDueDate',
	basis: 'legalFoundation',
	assignmentNo: 'assignmentNumber',
	ifd: 'integrationServiceOffice',
	service: 'service',
	advisor: 'advisor',
	sponsorGroup: 'assignmentSponsor',
	sponsorCategory: 'categorySponsor',
	disabilityType: 'careRelatedHandicap',
	institutionAtStart: 'institutionAtStart',
	institutionAtDate: 'institutionAtDate',
	institutionAtEnd: 'institutionAtEnd',
	schoolNumberAtStart: 'schoolNumberAtStart',
	schoolNumberAtDate: 'schoolNumberAtDate',
	schoolNumberAtEnd: 'schoolNumberAtEnd',
	socialSecurityStatusAtStart: 'socialSecurityStatusAtStart',
	socialSecurityStatusAtDate: 'socialSecurityStatusAtDate',
	socialSecurityStatusAtEnd: 'socialSecurityStatusAtEnd',
	disabilityTypeAtStart: 'disabilityTypeAtStart',
	disabilityTypeAtDate: 'disabilityTypeAtDate',
	disabilityTypeAtEnd: 'disabilityTypeAtEnd',
	disabilityProofAtStart: 'disabilityProofAtStart',
	disabilityProofAtDate: 'disabilityProofAtDate',
	disabilityProofAtEnd: 'disabilityProofAtEnd',
	gdbAtStart: 'gdbAtStart',
	gdbAtDate: 'gdbAtDate',
	gdbAtEnd: 'gdbAtEnd',
	lastThreeContactDates: 'lastThreeContactDates',
	contactTaskCount: 'contactTaskCount',
	impedimentsAtStart: 'impedimentsAtStart',
	impedimentsAtEnd: 'impedimentsAtEnd',
};

const FILTER_KEYS: FilterKey[] = [
	'clientName',
	'phase',
	'assignmentType',
	'careType',
	'initiatingOffice',
	'caseGroup',
	'basis',
	'ifd',
	'service',
	'advisor',
	'sponsorGroup',
	'sponsorCategory',
	'disabilityType',
];
const SEARCH_ONLY_SINGLE_FILTER_KEYS: FilterKey[] = ['clientName', 'sponsorCategory'];

const EMPTY_FILTER_VALUES = createRecordFromKeys(FILTER_KEYS, () => [] as string[]);
const EMPTY_FILTER_SEARCH = createRecordFromKeys(FILTER_KEYS, () => '');
const EMPTY_DATE_RANGE: DatabaseDateRangeValue = { start: null, end: null };

const STATIC_FILTER_OPTIONS: Partial<Record<FilterKey, DatabaseFilterOption[]>> = {
	phase: nameOptions([...DATABASE_PHASE_FILTER_OPTIONS]),
	assignmentType: nameOptions(['Hauptauftrag', 'Zusatzauftrag', 'Fachdienstliche Stellungnahme']),
};
const TAB_ID = 'assignments';
const SCHEMA_VIEW = 'assignments';
const SOURCE_FILTER_KEYS: Record<DatabaseFilterSourceKey, FilterKey> = {
	typeOfCare: 'careType',
	caseGroup: 'caseGroup',
	initiator: 'initiatingOffice',
	legalFoundation: 'basis',
	assignmentSponsor: 'sponsorGroup',
	disabilityType: 'disabilityType',
	integrationServiceOffice: 'ifd',
	service: 'service',
	advisorName: 'advisor',
};

interface AssignmentStoredState {
	pageIndex?: number;
	sortField?: AssignmentListSortField | null;
	sortDirection?: DatabaseSortDirection;
	rangeStart?: string | null;
	rangeEnd?: string | null;
	rangePreset?: string | null;
	filterValues?: Partial<Record<FilterKey, string[]>>;
	filterSearch?: Partial<Record<FilterKey, string>>;
	selectedColumns?: string[];
	dateRanges?: Partial<Record<string, { start: string | null; end: string | null }>>;
	referenceDate?: string | null;
}

const parseAssignmentStoredState = (raw: unknown): AssignmentStoredState | undefined =>
	typeof raw === 'object' && raw !== null ? (raw as AssignmentStoredState) : undefined;

const RANGE_PRESET_LABEL_KEYS: Record<DatabaseRangePreset, string> = {
	lastMonth: 'database_view.range_presets.last_month',
	lastQuarter: 'database_view.range_presets.last_quarter',
	lastYear: 'database_view.range_presets.last_year',
};
const RANGE_PRESET_ICONS: Record<DatabaseRangePreset, string> = {
	lastMonth: 'calendar_view_month',
	lastQuarter: 'date_range',
	lastYear: 'calendar_today',
};
const RANGE_PRESET_PLACEHOLDER_KEY = 'database_view.range_presets.placeholder';
const PAGINATOR_PREFERENCE_KEY = 'database-assignments-list';
const SEARCH_DEBOUNCE_MS = 400;
const SEARCH_MIN_CHARS = 3;

@Component({
	selector: 'lvr-database-assignments-list',
	templateUrl: './database-assignments-list.html',
	styleUrl: './database-assignments-list.scss',
	providers: [
		provideCategoryApi,
		provideLegalFoundationApi,
		provideDisabilityTypeApi,
		provideIsoApi,
		provideDatabaseViewApi,
		provideUserApi,
		matDateProviders,
	],
	imports: [
		MatIcon,
		MatIconButton,
		MatProgressBarModule,
		MatMenuModule,
		MatFormFieldModule,
		MatInputModule,
		MatDatepickerModule,
		MatTableModule,
		MatTooltipModule,
		TranslocoPipe,
		Paginator,
		RouterLink,
		LvrButton,
		DatabaseColumnSelector,
		DatabaseMultiSelectFilter,
		DatabaseDateRangeFilter,
		DatabaseReferenceDateFilter,
	],
})
export class DatabaseAssignmentsList implements OnDestroy {
	private readonly environment = inject(ENVIRONMENT);
	private readonly auth = inject(MsalService, { optional: true });
	private readonly storage = inject(LocalStorageService, { optional: true });
	private readonly viewStorage = inject(DatabaseViewStorage);
	private readonly dateFormat = inject(DatabaseDateFormatService);
	private readonly categoriesApi = inject(CATEGORIES_API);
	private readonly legalFoundationApi = inject(LEGAL_FOUNDATION_SERVICE);
	private readonly disabilityTypeApi = inject(DISABILITY_TYPE_SERVICE);
	private readonly isoService = inject(INTEGRATION_OFFICE_SERVICE);
	private readonly userService = inject(USER_SERVICE);
	private readonly session = inject(SessionManagerService);
	private readonly representation = inject(REPRESENTATION_MODE, { optional: true });
	private readonly filterPanels = inject(DatabaseFilterPanelRegistry);
	private readonly destroyRef = inject(DestroyRef);
	private readonly databaseViewApi = inject(DATABASE_VIEW_API);
	private readonly tenantFeatures = inject(TENANT_FEATURES);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly errorService = inject(ERROR_PROVIDER);
	private readonly transloco = inject(TranslocoService);

	private readonly selector = viewChild(DatabaseColumnSelector);

	private readonly filterOptionsRange = this.environment.pagination.range;
	private readonly filterOptionsOffset = this.environment.pagination.offset;
	private readonly dateSortFields: AssignmentListSortField[] = [
		'counselingStart',
		'assignmentStart',
		'deregistrationDate',
		'reportDue',
		'lastThreeContactDates',
	];
	private readonly stickyColumns: ColumnKey[] = ['clientName', 'phase', 'assignmentType', 'careType'];
	public readonly rangePresets = Object.keys(RANGE_PRESET_LABEL_KEYS) as DatabaseRangePreset[];
	public readonly rangePresetLabelKeys = RANGE_PRESET_LABEL_KEYS;
	public readonly rangePresetIcons = RANGE_PRESET_ICONS;
	public readonly rangePresetPlaceholderKey = RANGE_PRESET_PLACEHOLDER_KEY;
	private readonly allColumns: AssignmentColumn[] = [
		{
			key: 'clientName',
			labelKey: 'database_view.assignments.columns.client',
			sortField: 'clientName',
			filter: { type: 'single', key: 'clientName' },
		},
		{
			key: 'phase',
			labelKey: 'database_view.assignments.columns.phase',
			sortField: 'phase',
			filter: { type: 'single', key: 'phase' },
		},
		{
			key: 'assignmentType',
			labelKey: 'database_view.assignments.columns.assignment_type',
			sortField: 'assignmentType',
			filter: { type: 'single', key: 'assignmentType' },
		},
		{
			key: 'careType',
			labelKey: 'database_view.assignments.columns.care_type',
			sortField: 'careType',
			filter: { type: 'single', key: 'careType' },
		},
		{
			key: 'initiatingOffice',
			labelKey: 'database_view.assignments.columns.initiating_office',
			sortField: 'initiatingOffice',
			filter: { type: 'single', key: 'initiatingOffice' },
		},
		{
			key: 'caseGroup',
			labelKey: 'database_view.assignments.columns.case_group',
			sortField: 'caseGroup',
			filter: { type: 'single', key: 'caseGroup' },
		},
		{
			key: 'basis',
			labelKey: 'database_view.assignments.columns.basis',
			sortField: 'basis',
			filter: { type: 'single', key: 'basis' },
		},
		{
			key: 'ifd',
			labelKey: 'database_view.assignments.columns.ifd',
			sortField: 'ifd',
			filter: { type: 'single', key: 'ifd' },
		},
		{
			key: 'service',
			labelKey: 'database_view.assignments.columns.service',
			sortField: 'service',
			filter: { type: 'single', key: 'service' },
		},
		{
			key: 'advisor',
			labelKey: 'database_view.assignments.columns.advisor',
			sortField: 'advisor',
			filter: { type: 'single', key: 'advisor' },
		},
		{
			key: 'sponsorGroup',
			labelKey: 'database_view.assignments.columns.sponsor_group',
			sortField: 'sponsorGroup',
			filter: { type: 'single', key: 'sponsorGroup' },
		},
		{
			key: 'sponsorCategory',
			labelKey: 'database_view.assignments.columns.sponsor_category',
			sortField: 'sponsorCategory',
			filter: { type: 'single', key: 'sponsorCategory' },
		},
		{
			key: 'disabilityType',
			labelKey: 'database_view.assignments.columns.disability_type',
			sortField: 'disabilityType',
			filter: { type: 'single', key: 'disabilityType' },
		},
		{
			key: 'counselingStart',
			labelKey: 'database_view.assignments.columns.counseling_start',
			sortField: 'counselingStart',
			filter: { type: 'dateRange', fieldId: 'startOfConsultation' },
		},
		{
			key: 'assignmentStart',
			labelKey: 'database_view.assignments.columns.assignment_start',
			sortField: 'assignmentStart',
			filter: { type: 'dateRange', fieldId: 'startOfCare' },
		},
		{
			key: 'deregistrationDate',
			labelKey: 'database_view.assignments.columns.deregistration_date',
			sortField: 'deregistrationDate',
			filter: { type: 'dateRange', fieldId: 'cancellationDate' },
		},
		{ key: 'assignmentNo', labelKey: 'database_view.assignments.columns.assignment_no', sortField: 'assignmentNo' },
		{ key: 'reportDue', labelKey: 'database_view.assignments.columns.report_due', sortField: 'reportDue' },
		{ key: 'institutionAtStart', labelKey: 'database_view.assignments.columns.institution_at_start' },
		{ key: 'institutionAtDate', labelKey: 'database_view.assignments.columns.institution_at_date' },
		{ key: 'institutionAtEnd', labelKey: 'database_view.assignments.columns.institution_at_end' },
		{ key: 'schoolNumberAtStart', labelKey: 'database_view.assignments.columns.school_number_at_start' },
		{ key: 'schoolNumberAtDate', labelKey: 'database_view.assignments.columns.school_number_at_date' },
		{ key: 'schoolNumberAtEnd', labelKey: 'database_view.assignments.columns.school_number_at_end' },
		{
			key: 'socialSecurityStatusAtStart',
			labelKey: 'database_view.assignments.columns.social_security_status_at_start',
		},
		{ key: 'socialSecurityStatusAtDate', labelKey: 'database_view.assignments.columns.social_security_status_at_date' },
		{ key: 'socialSecurityStatusAtEnd', labelKey: 'database_view.assignments.columns.social_security_status_at_end' },
		{ key: 'disabilityTypeAtStart', labelKey: 'database_view.assignments.columns.disability_type_at_start' },
		{ key: 'disabilityTypeAtDate', labelKey: 'database_view.assignments.columns.disability_type_at_date' },
		{ key: 'disabilityTypeAtEnd', labelKey: 'database_view.assignments.columns.disability_type_at_end' },
		{ key: 'disabilityProofAtStart', labelKey: 'database_view.assignments.columns.disability_proof_at_start' },
		{ key: 'disabilityProofAtDate', labelKey: 'database_view.assignments.columns.disability_proof_at_date' },
		{ key: 'disabilityProofAtEnd', labelKey: 'database_view.assignments.columns.disability_proof_at_end' },
		{ key: 'gdbAtStart', labelKey: 'database_view.assignments.columns.gdb_at_start' },
		{ key: 'gdbAtDate', labelKey: 'database_view.assignments.columns.gdb_at_date' },
		{ key: 'gdbAtEnd', labelKey: 'database_view.assignments.columns.gdb_at_end' },
		{
			key: 'lastThreeContactDates',
			labelKey: 'database_view.assignments.columns.last_three_contact_dates',
			sortField: 'lastThreeContactDates',
		},
		{ key: 'contactTaskCount', labelKey: 'database_view.assignments.columns.contact_task_count' },
		{ key: 'impedimentsAtStart', labelKey: 'database_view.assignments.columns.impediments_at_start' },
		{ key: 'impedimentsAtEnd', labelKey: 'database_view.assignments.columns.impediments_at_end' },
	];

	private lastCaseGroupVisibility: boolean | undefined;
	private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;
	/** Search state of the rows currently shown; a debounced search equal to it is redundant. */
	private loadedSearchSignature = '';
	private readonly loadingCounter = signal(0);
	/** Filter-option preloads and their 30-minute reloads; they lock filter, sort, export and pagination. */
	private readonly preloadPending = signal(0);
	/** The newest list request; a new list request or a representation change aborts it. */
	private listAbort: AbortController | null = null;
	private lastRepresentationKey: string | null = null;
	private readonly schema = signal<DatabaseViewSchema | null>(null);
	private readonly schemaReady = signal(false);
	private readonly requestSequence = signal(0);
	private readonly tablePage = signal<{
		entities: DatabaseViewAssignmentResponse[];
		totalEntities: number;
		offset: number;
		range: number;
	}>({
		entities: [],
		totalEntities: 0,
		offset: 0,
		range: 0,
	});

	public readonly selectedColumnIds = signal<string[]>([]);
	public readonly applyingColumns = signal(false);
	public readonly pageIndex = signal(0);
	public readonly pageSize = signal(this.environment.defaultPageSize);
	public readonly sortField = signal<AssignmentListSortField | null>('reportDue');
	public readonly sortDirection = signal<DatabaseSortDirection>('desc');
	public readonly activeSortField = signal<AssignmentListSortField>('reportDue');
	public readonly filterValues = signal<Record<FilterKey, string[]>>({ ...EMPTY_FILTER_VALUES });
	public readonly filterSearch = signal<Record<FilterKey, string>>({ ...EMPTY_FILTER_SEARCH });
	public readonly apiFilterOptions = signal<Partial<Record<FilterKey, DatabaseFilterOption[]>>>({
		...STATIC_FILTER_OPTIONS,
	});
	public readonly filterOptionValueMap = computed(() => {
		const options = this.apiFilterOptions();
		const next: Partial<Record<FilterKey, string[]>> = {};
		for (const key of FILTER_KEYS) next[key] = filterOptionValues(options[key] ?? []);
		return next;
	});
	public readonly filterOptionLabelMap = computed(() => {
		const options = this.apiFilterOptions();
		const next: Partial<Record<FilterKey, Record<string, string>>> = {};
		for (const key of FILTER_KEYS) next[key] = filterOptionLabels(options[key] ?? []);
		return next;
	});
	public readonly unavailableFilterKeys = signal<ReadonlySet<FilterKey>>(new Set());
	public readonly rangeStart = signal<Date | null>(null);
	public readonly rangeEnd = signal<Date | null>(null);
	public readonly rangePreset = signal<DatabaseRangePreset | null>(null);
	/** Column date ranges keyed by schema field id; each column filters independently of the global Zeitraum. */
	public readonly dateRanges = signal<Partial<Record<AssignmentDateRangeFieldId, DatabaseDateRangeValue>>>({});
	/** Reference date (Stichtag); only sent while an atDate column is selected. */
	public readonly referenceDate = signal<Date | null>(null);
	public readonly referenceDateAvailable = computed(() => isReferenceDateAvailable(this.selectedColumnIds()));
	public readonly exporting = signal(false);

	public readonly showCaseGroup = computed(() => this.tenantFeatures.caseGroupEnabled());
	public columns: AssignmentColumn[] = this.showCaseGroup()
		? this.allColumns
		: this.allColumns.filter((column) => column.key !== 'caseGroup');
	public displayedColumns: ColumnKey[] = this.columns.map((column) => column.key);
	public readonly requestPayload = computed(() =>
		buildDatabaseViewAssignmentRequest(
			{
				rangeStart: formatDateToISOString(this.rangeStart()) ?? '',
				rangeEnd: formatDateToISOString(this.rangeEnd()) ?? '',
				filters: this.effectiveFilterValues(),
				filterSearch: this.effectiveFilterSearch(),
				sortField: this.sortField(),
				sortDirection: this.sortDirection(),
				dateRanges: this.queryDateRanges(),
			},
			this.schema(),
			this.selectedColumnIds(),
			this.referenceDate(),
		),
	);
	public readonly columnOptions = computed<DatabaseColumnOption[]>(() => {
		const schema = this.schema();
		if (!schema) return [];
		const showCaseGroup = this.showCaseGroup();
		return schema.columns.flatMap(({ id: schemaId }) => {
			const column = this.allColumns.find(
				(candidate) =>
					(showCaseGroup || candidate.key !== 'caseGroup') && ASSIGNMENT_SCHEMA_IDS[candidate.key] === schemaId,
			);
			return column ? [{ schemaId, renderKey: column.key, label: this.transloco.translate(column.labelKey) }] : [];
		});
	});
	private readonly defaultColumnIds = computed(() => this.columnOptions().map((option) => option.schemaId));
	public readonly canExport = computed(() => this.schemaReady() && !this.isPageLoading());
	public readonly tableRows = computed(() => this.tablePage().entities.map((x) => this.mapApiAssignmentToRow(x)));
	public readonly pageRows = computed(() => this.tableRows());
	public readonly totalItems = computed(() => this.tablePage().totalEntities);
	/** Filter, sort and pagination lock while the schema, a preload or a column change is pending. */
	public readonly controlsLocked = computed(
		() => !this.schemaReady() || this.applyingColumns() || this.preloadPending() > 0,
	);
	/** Column selection also waits for list requests, so the column set matches the shown rows. */
	public readonly columnSelectorLocked = computed(() => this.controlsLocked() || this.loadingCounter() > 0);
	public readonly isPageLoading = computed(() => this.loadingCounter() > 0 || this.controlsLocked());
	public readonly rangePresetButtonKey = computed(() => {
		const preset = this.rangePreset();
		return preset ? RANGE_PRESET_LABEL_KEYS[preset] : RANGE_PRESET_PLACEHOLDER_KEY;
	});
	public readonly selectedRangeLabel = computed(() =>
		this.dateFormat.formatPeriodRange(this.rangeStart(), this.rangeEnd()),
	);

	public constructor() {
		this.hydrateStateFromStorage();
		this.normalizeSearchOnlyFilters();
		this.destroyRef.onDestroy(this.session.onInvalidate(() => this.withdrawForSessionEnd()));
		effect(() => {
			const state = this.representation?.mode();
			const key = state ? `${state.active}|${state.representedUserId ?? ''}` : '';
			untracked(() => {
				if (this.lastRepresentationKey !== null && this.lastRepresentationKey !== key) this.abortListRequest();
				this.lastRepresentationKey = key;
			});
		});
		effect(() => {
			const showCaseGroup = this.showCaseGroup();
			const visibilityChanged =
				this.lastCaseGroupVisibility !== undefined && this.lastCaseGroupVisibility !== showCaseGroup;
			this.lastCaseGroupVisibility = showCaseGroup;
			this.columns = showCaseGroup ? this.allColumns : this.allColumns.filter((column) => column.key !== 'caseGroup');
			untracked(() => {
				// Column options are empty until the schema loads; reconciling earlier would drop the restored selection.
				if (!this.schema()) return;
				this.selectedColumnIds.update((selected) =>
					reconcileColumnIds(this.columnOptions(), selected, this.defaultColumnIds()),
				);
				this.syncReferenceDate(this.selectedColumnIds());
			});
			this.updateDisplayedColumns();
			if (visibilityChanged) untracked(() => void this.loadAssignments());
		});
		afterNextRender(() => {
			void this.loadFilterOptions();
			void this.loadAssignments();
		});
	}

	/** Drops shown rows, pending search and in-flight responses when the session ends. */
	private withdrawForSessionEnd() {
		this.requestSequence.update((sequence) => sequence + 1);
		this.abortListRequest();
		if (this.searchDebounceTimer != null) {
			clearTimeout(this.searchDebounceTimer);
			this.searchDebounceTimer = null;
		}
		this.tablePage.update((page) => ({ ...page, entities: [], totalEntities: 0 }));
		this.loadedSearchSignature = '';
		this.filterPanels.closeActive();
	}

	private abortListRequest() {
		this.requestSequence.update((sequence) => sequence + 1);
		this.listAbort?.abort();
		this.listAbort = null;
	}

	public ngOnDestroy() {
		this.abortListRequest();
		if (this.searchDebounceTimer != null) {
			clearTimeout(this.searchDebounceTimer);
			this.searchDebounceTimer = null;
		}
	}

	public async export() {
		if (this.exporting() || !this.canExport()) return;
		await runDatabaseCsvExport({
			exporting: this.exporting,
			fetchCsv: () => this.databaseViewApi.getAssignmentsCsv(this.requestPayload()),
			filename: 'auftraege_liste.csv',
			translate: (key) => this.transloco.translate(key),
			notify: (notification) => this.ntfy.addNotification(notification),
		});
	}

	public async applyColumns(candidateIds: readonly string[]) {
		if (this.columnSelectorLocked()) return;
		const nextIds = reconcileColumnIds(this.columnOptions(), candidateIds, this.selectedColumnIds());
		if (!nextIds.length || areColumnIdsEqual(nextIds, this.selectedColumnIds())) return;
		const removedColumns = this.columns.filter((column) => !nextIds.includes(ASSIGNMENT_SCHEMA_IDS[column.key]));
		const {
			filterValues: nextFilterValues,
			filterSearch: nextFilterSearch,
			sortField: nextSortField,
			sortDirection: nextSortDirection,
			resetSort,
		} = resetStateForRemovedColumns(
			{
				filterValues: this.filterValues(),
				filterSearch: this.filterSearch(),
				sortField: this.sortField(),
				sortDirection: this.sortDirection(),
			},
			removedColumns.map((column) => ({
				filterKey: column.filter?.type === 'single' ? column.filter.key : undefined,
				sortField: column.sortField,
			})),
			'desc',
		);
		const sequence = this.requestSequence() + 1;
		this.requestSequence.set(sequence);
		this.applyingColumns.set(true);
		const nextDateRanges = Object.fromEntries(
			Object.entries(this.dateRanges()).filter(([fieldId]) => nextIds.includes(fieldId)),
		);
		try {
			const response = await this.databaseViewApi.getAssignments(
				buildDatabaseViewAssignmentRequest(
					{
						rangeStart: formatDateToISOString(this.rangeStart()) ?? '',
						rangeEnd: formatDateToISOString(this.rangeEnd()) ?? '',
						filters: this.effectiveFilterValues(nextFilterValues),
						filterSearch: this.effectiveFilterSearch(nextFilterSearch),
						sortField: nextSortField,
						sortDirection: nextSortDirection,
						dateRanges: this.queryDateRanges(nextDateRanges, nextIds),
					},
					this.schema(),
					[...nextIds],
					this.referenceDate(),
				),
				0,
				this.pageSize(),
			);
			if (sequence !== this.requestSequence()) return;
			this.loadedSearchSignature = JSON.stringify(this.effectiveFilterSearch(nextFilterSearch));
			this.selectedColumnIds.set([...nextIds]);
			this.syncReferenceDate(nextIds);
			this.dateRanges.set(nextDateRanges);
			this.filterValues.set(nextFilterValues);
			this.filterSearch.set(nextFilterSearch);
			this.sortField.set(nextSortField);
			this.sortDirection.set(nextSortDirection);
			if (resetSort) this.activeSortField.set('reportDue');
			this.updateDisplayedColumns();
			this.pageIndex.set(0);
			this.tablePage.set(response);
			this.persistStateToStorage();
			this.selector()?.close();
		} catch {
			this.ntfy.addNotification(createErrorNotification(this.transloco.translate('database_view.columns.apply_error')));
		} finally {
			// Only one column request runs at a time (guarded above), so this request owns the flag.
			this.applyingColumns.set(false);
		}
	}

	public setSort(field: AssignmentListSortField, direction: DatabaseSortDirection) {
		if (this.controlsLocked()) return;
		this.sortField.set(field);
		this.sortDirection.set(direction);
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadAssignments();
	}

	public setActiveSortField(field: AssignmentListSortField) {
		this.activeSortField.set(field);
	}

	public isSortOptionActive(field: AssignmentListSortField, direction: DatabaseSortDirection) {
		return this.sortField() === field && this.sortDirection() === direction;
	}

	public sortIcon(field: AssignmentListSortField | null | undefined) {
		if (field !== this.sortField() || !field) return 'swap_vert';
		return this.sortDirection() === 'asc' ? 'arrow_upward' : 'arrow_downward';
	}

	public sortLabel(direction: DatabaseSortDirection) {
		const field = this.activeSortField();
		if (field === 'phase') {
			return direction === 'asc'
				? 'database_view.assignments.sort.phase_ascending'
				: 'database_view.assignments.sort.phase_descending';
		}
		if (field === 'assignmentNo') {
			return direction === 'asc'
				? 'database_view.assignments.sort.assignment_no_ascending'
				: 'database_view.assignments.sort.assignment_no_descending';
		}
		if (this.dateSortFields.includes(field)) {
			return direction === 'asc'
				? 'database_view.assignments.sort.date_ascending'
				: 'database_view.assignments.sort.date_descending';
		}
		return direction === 'asc'
			? 'database_view.assignments.sort.alphabetical_ascending'
			: 'database_view.assignments.sort.alphabetical_descending';
	}

	public setRangeStart(value: Date | null) {
		this.rangePreset.set(null);
		this.rangeStart.set(value);
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadAssignments();
	}

	public setRangeEnd(value: Date | null) {
		this.rangePreset.set(null);
		this.rangeEnd.set(value);
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadAssignments();
	}

	public clearDateRange(event?: Event) {
		event?.stopPropagation();
		if (!this.rangeStart() && !this.rangeEnd()) return;
		this.rangePreset.set(null);
		this.rangeStart.set(null);
		this.rangeEnd.set(null);
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadAssignments();
	}

	public dateRangeFieldId(column: AssignmentColumn): AssignmentDateRangeFieldId | null {
		return column.filter?.type === 'dateRange' ? (column.filter.fieldId as AssignmentDateRangeFieldId) : null;
	}

	public columnDateRange(fieldId: AssignmentDateRangeFieldId): DatabaseDateRangeValue {
		return this.dateRanges()[fieldId] ?? EMPTY_DATE_RANGE;
	}

	public isDateRangeUnavailable(fieldId: AssignmentDateRangeFieldId): boolean {
		const column = this.schema()?.columns.find((candidate) => candidate.id === fieldId);
		return !column?.filterOperations.includes('between');
	}

	public setColumnDateRange(fieldId: AssignmentDateRangeFieldId, value: DatabaseDateRangeValue) {
		this.dateRanges.update((ranges) => {
			const next = { ...ranges };
			if (value.start || value.end) next[fieldId] = value;
			else delete next[fieldId];
			return next;
		});
		this.afterColumnDateRangeChange();
	}

	public clearColumnDateRange(fieldId: AssignmentDateRangeFieldId) {
		if (!this.dateRanges()[fieldId]) return;
		this.dateRanges.update((ranges) => {
			const next = { ...ranges };
			delete next[fieldId];
			return next;
		});
		this.afterColumnDateRangeChange();
	}

	private afterColumnDateRangeChange() {
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadAssignments();
	}

	public applyRangePreset(preset: DatabaseRangePreset) {
		const range = resolveDatabaseRangePreset(preset, new Date());
		this.rangeStart.set(range.start);
		this.rangeEnd.set(range.end);
		this.rangePreset.set(preset);
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadAssignments();
	}

	public setFilterSearch(key: FilterKey, value: string) {
		if (this.controlsLocked()) return;
		const currentValue = this.filterSearch()[key] ?? '';
		if (currentValue === value) return;
		this.filterSearch.update((s) => ({ ...s, [key]: value }));
		this.pageIndex.set(0);
		this.persistStateToStorage();
		this.scheduleSearchLoad();
	}

	public toggleFilterValue(key: FilterKey, value: string, checked: boolean) {
		if (this.controlsLocked()) return;
		this.filterValues.update((state) => toggleFilterSelection(state, key, value, checked));
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadAssignments();
	}

	public applyMultiFilterSelection(
		key: FilterKey,
		values: readonly string[],
		filterPanel: Pick<DatabaseMultiSelectFilter, 'closePanel'>,
	) {
		if (this.controlsLocked()) return;
		this.filterValues.update((state) => ({ ...state, [key]: [...values] }));
		this.pageIndex.set(0);
		this.persistStateToStorage();
		filterPanel.closePanel();
		void this.loadAssignments();
	}

	public onMultiFilterPanelClosed(key: FilterKey) {
		this.filterSearch.update((state) => ({ ...state, [key]: '' }));
	}

	public filterOptions(key: FilterKey) {
		return this.filterOptionValueMap()[key] ?? [];
	}

	public filterLabels(key: FilterKey) {
		return this.filterOptionLabelMap()[key] ?? {};
	}

	public isFilterUnavailable(key: FilterKey | null) {
		return key !== null && this.unavailableFilterKeys().has(key);
	}

	public singleFilterKey(column: AssignmentColumn): FilterKey | null {
		return column.filter?.type === 'single' ? column.filter.key : null;
	}

	public isSearchOnlySingleFilter(key: FilterKey) {
		return SEARCH_ONLY_SINGLE_FILTER_KEYS.includes(key);
	}

	public resetAll() {
		if (this.controlsLocked()) return;
		this.filterValues.set({ ...EMPTY_FILTER_VALUES });
		this.filterSearch.set({ ...EMPTY_FILTER_SEARCH });
		this.rangePreset.set(null);
		this.rangeStart.set(null);
		this.rangeEnd.set(null);
		this.dateRanges.set({});
		this.referenceDate.set(null);
		this.sortField.set('reportDue');
		this.sortDirection.set('desc');
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadAssignments();
	}

	public handlePageEvent(event: PageEvent) {
		if (this.controlsLocked()) return;
		this.pageIndex.set(event.pageIndex);
		this.pageSize.set(event.pageSize);
		this.persistStateToStorage();
		void this.loadAssignments();
	}

	public getCellValue(row: AssignmentRow, columnKey: ColumnKey) {
		if (columnKey === 'deregistrationDate') return row.deregistrationDate || '-';
		return String(row[columnKey as keyof AssignmentRow] ?? '');
	}

	public isStickyColumn(columnKey: ColumnKey) {
		return this.stickyColumns.includes(columnKey);
	}

	public hasClientLink(row: AssignmentRow) {
		return row.clientId > 0;
	}

	public hasAssignmentLink(row: AssignmentRow) {
		return row.clientId > 0 && row.assignmentId > 0;
	}

	public hasAdvisorLink(row: AssignmentRow): boolean {
		return !!row.advisorObjectId;
	}

	public hasIfdOverviewLink(row: AssignmentRow) {
		return row.ifdId > 0;
	}

	public getAssignmentDetailsLink(row: AssignmentRow): unknown[] {
		const assignmentType = row.assignmentType.trim().toLowerCase();
		const baseLink = ['/desk/client', row.clientId, 'assignments', row.assignmentId];

		if (assignmentType === 'zusatzauftrag') {
			return [...baseLink, 'additional'];
		}

		if (assignmentType === 'fachdienstliche stellungnahme') {
			return [...baseLink, 'fds'];
		}

		return baseLink;
	}

	private async loadAssignments(): Promise<'loaded' | 'stale' | 'failed'> {
		const bypassCache = this.viewStorage.shouldBypassCache(TAB_ID);
		this.listAbort?.abort();
		const controller = new AbortController();
		this.listAbort = controller;
		const sequence = this.requestSequence() + 1;
		this.requestSequence.set(sequence);
		this.loadingCounter.update((count) => count + 1);
		try {
			if (!this.schema()) {
				this.schema.set(
					await this.viewStorage.resolveSchema(
						TAB_ID,
						SCHEMA_VIEW,
						() => this.databaseViewApi.getAssignmentsSchema(),
						bypassCache,
					),
				);
				this.selectedColumnIds.set(
					reconcileColumnIds(this.columnOptions(), this.selectedColumnIds(), this.defaultColumnIds()),
				);
				this.syncReferenceDate(this.selectedColumnIds());
				this.updateDisplayedColumns();
				this.schemaReady.set(true);
				this.persistStateToStorage();
			}
			const searchSignature = JSON.stringify(this.effectiveFilterSearch());
			const response = await this.databaseViewApi.getAssignments(
				this.requestPayload(),
				this.pageIndex(),
				this.pageSize(),
				controller.signal,
			);
			if (sequence !== this.requestSequence()) return 'stale';
			this.tablePage.set(response);
			this.loadedSearchSignature = searchSignature;
			this.viewStorage.markTabRefreshed(TAB_ID);
			return 'loaded';
		} catch (error) {
			if (controller.signal.aborted || sequence !== this.requestSequence()) return 'stale';
			void this.errorService.handleError(error, true);
			this.ntfy.addNotification(createErrorNotification(this.transloco.translate('database_view.load_error')));
			return 'failed';
		} finally {
			if (this.listAbort === controller) this.listAbort = null;
			this.loadingCounter.update((count) => Math.max(0, count - 1));
		}
	}

	private async loadFilterOptions() {
		const bypassCache = this.viewStorage.shouldBypassCache(TAB_ID);
		this.preloadPending.update((count) => count + 1);
		try {
			const sources = await this.viewStorage.resolveSourceOptions(
				TAB_ID,
				DATABASE_FILTER_SOURCE_KEYS,
				(missing) =>
					loadDatabaseFilterSourceOptions(
						{
							categoriesApi: this.categoriesApi,
							legalFoundationApi: this.legalFoundationApi,
							databaseViewApi: this.databaseViewApi,
							disabilityTypeApi: this.disabilityTypeApi,
							isoService: this.isoService,
							userService: this.userService,
						},
						this.filterOptionsOffset,
						this.filterOptionsRange,
						missing,
					),
				bypassCache,
			);

			const next: Partial<Record<FilterKey, DatabaseFilterOption[]>> = { ...STATIC_FILTER_OPTIONS };
			const unavailable = new Set<FilterKey>();
			for (const source of DATABASE_FILTER_SOURCE_KEYS) {
				const values = sources[source];
				const filterKey = SOURCE_FILTER_KEYS[source];
				if (values === null) unavailable.add(filterKey);
				else next[filterKey] = values;
			}

			if (sources.advisorName === null)
				this.ntfy.addNotification(
					createErrorNotification(this.transloco.translate('database_view.filters.user_options_error')),
				);

			this.unavailableFilterKeys.set(unavailable);
			this.apiFilterOptions.set(next);
		} finally {
			this.preloadPending.update((count) => Math.max(0, count - 1));
		}
	}

	private updateDisplayedColumns() {
		this.displayedColumns = this.selectedColumnIds().flatMap((schemaId) =>
			this.columns.filter((column) => ASSIGNMENT_SCHEMA_IDS[column.key] === schemaId).map((column) => column.key),
		);
	}

	private effectiveFilterValues(filterValues = this.filterValues()) {
		const filters = { ...filterValues };
		if (!this.showCaseGroup()) {
			filters.caseGroup = [];
		}
		if (filters.phase?.includes('P1')) {
			filters.phase = [...new Set([...filters.phase, 'P0'])];
		}
		return filters;
	}

	private effectiveFilterSearch(filterSearch = this.filterSearch()) {
		const raw = filterSearch;
		const next = { ...raw };
		for (const key of FILTER_KEYS) {
			const value = (raw[key] ?? '').trim();
			next[key] = value.length === 0 || value.length >= SEARCH_MIN_CHARS ? value : '';
		}
		if (!this.showCaseGroup()) {
			next.caseGroup = '';
		}
		return next;
	}

	private normalizeSearchOnlyFilters() {
		this.filterValues.update((state) => {
			const next = { ...state };
			for (const key of SEARCH_ONLY_SINGLE_FILTER_KEYS) {
				next[key] = [];
			}
			return next;
		});
	}

	private scheduleSearchLoad() {
		if (this.searchDebounceTimer != null) {
			clearTimeout(this.searchDebounceTimer);
		}

		this.searchDebounceTimer = setTimeout(() => {
			this.searchDebounceTimer = null;
			const signature = JSON.stringify(this.effectiveFilterSearch());
			if (signature === this.loadedSearchSignature) return;
			void this.loadAssignments();
		}, SEARCH_DEBOUNCE_MS);
	}

	private mapApiAssignmentToRow(assignment: DatabaseViewAssignmentResponse): AssignmentRow {
		return {
			assignmentId: assignment.assignment?.id ?? 0,
			clientId: assignment.client?.id ?? 0,
			ifdId: assignment.integrationServiceOffice?.id ?? 0,
			advisorId: assignment.advisor?.id ?? 0,
			advisorObjectId: assignment.advisor?.guid ?? undefined,
			clientName: assignment.client?.text || '-',
			phase: this.mapPhaseForDisplay(assignment.phase?.text),
			assignmentType: assignment.assignment?.text || '-',
			careType: assignment.typeOfCare?.text || '-',
			initiatingOffice: assignment.initiator?.text || '-',
			caseGroup: assignment.caseGroup?.text || '-',
			counselingStart: this.dateFormat.formatResponseDate(assignment.startOfConsultation),
			assignmentStart: this.dateFormat.formatResponseDate(assignment.startOfCare),
			deregistrationDate: this.dateFormat.formatResponseDate(assignment.cancellationDate),
			basis: assignment.legalFoundation?.text || '-',
			assignmentNo: assignment.assignmentNumber || '-',
			reportDue: this.dateFormat.formatResponseDate(assignment.reportDueDate),
			ifd: assignment.integrationServiceOffice?.text || '-',
			service: assignment.service?.text || '-',
			advisor: assignment.advisor?.text || '-',
			sponsorGroup: assignment.assignmentSponsor?.text || '-',
			sponsorCategory: assignment.categorySponsor?.text || '-',
			disabilityType: assignment.careRelatedHandicap?.text || '-',
			institutionAtStart: assignment.institutionAtStart || '-',
			institutionAtDate: assignment.institutionAtDate || '-',
			institutionAtEnd: assignment.institutionAtEnd || '-',
			schoolNumberAtStart: assignment.schoolNumberAtStart || '-',
			schoolNumberAtDate: assignment.schoolNumberAtDate || '-',
			schoolNumberAtEnd: assignment.schoolNumberAtEnd || '-',
			socialSecurityStatusAtStart: assignment.socialSecurityStatusAtStart || '-',
			socialSecurityStatusAtDate: assignment.socialSecurityStatusAtDate || '-',
			socialSecurityStatusAtEnd: assignment.socialSecurityStatusAtEnd || '-',
			disabilityTypeAtStart: assignment.disabilityTypeAtStart || '-',
			disabilityTypeAtDate: assignment.disabilityTypeAtDate || '-',
			disabilityTypeAtEnd: assignment.disabilityTypeAtEnd || '-',
			disabilityProofAtStart: assignment.disabilityProofAtStart || '-',
			disabilityProofAtDate: assignment.disabilityProofAtDate || '-',
			disabilityProofAtEnd: assignment.disabilityProofAtEnd || '-',
			gdbAtStart: assignment.gdbAtStart || '-',
			gdbAtDate: assignment.gdbAtDate || '-',
			gdbAtEnd: assignment.gdbAtEnd || '-',
			lastThreeContactDates: assignment.lastThreeContactDates || '-',
			contactTaskCount: assignment.contactTaskCount ?? '-',
			impedimentsAtStart: assignment.impedimentsAtStart.join(', ') || '-',
			impedimentsAtEnd: assignment.impedimentsAtEnd.join(', ') || '-',
		};
	}

	private mapPhaseForDisplay(value?: string | null) {
		return value === 'P0' ? 'P1' : value || '-';
	}

	private parseDateParam(value: string | null) {
		if (!value) return null;
		const parsed = new Date(value);
		return Number.isNaN(parsed.getTime()) ? null : parsed;
	}

	private isSortField(value: string | null): value is AssignmentListSortField {
		return this.columns.some((column) => column.sortField === value);
	}

	private isSortDirection(value: string | null): value is DatabaseSortDirection {
		return value === 'asc' || value === 'desc';
	}

	private hydrateStateFromStorage() {
		const savedPageSize = this.getSavedPageSize();
		if (savedPageSize) this.pageSize.set(savedPageSize);

		const parsed = this.viewStorage.readListState(TAB_ID, parseAssignmentStoredState);
		if (!parsed) return;

		if (parsed.pageIndex != null) this.pageIndex.set(parsed.pageIndex);
		if (parsed.sortField === null) {
			this.sortField.set(null);
			this.activeSortField.set('reportDue');
		} else if (parsed.sortField && this.isSortField(parsed.sortField)) {
			this.sortField.set(parsed.sortField);
			this.activeSortField.set(parsed.sortField);
		}
		if (parsed.sortDirection && this.isSortDirection(parsed.sortDirection)) {
			this.sortDirection.set(parsed.sortDirection);
		}
		const restoredStart = this.parseDateParam(parsed.rangeStart ?? null);
		const restoredEnd = this.parseDateParam(parsed.rangeEnd ?? null);
		this.rangeStart.set(restoredStart);
		this.rangeEnd.set(restoredEnd);
		this.rangePreset.set(
			isDatabaseRangePreset(parsed.rangePreset) && restoredStart && restoredEnd ? parsed.rangePreset : null,
		);
		const restoredFilterValues = { ...EMPTY_FILTER_VALUES };
		const restoredFilterSearch = { ...EMPTY_FILTER_SEARCH };
		for (const key of FILTER_KEYS) {
			restoredFilterValues[key] = parsed.filterValues?.[key] ?? [];
			restoredFilterSearch[key] = parsed.filterSearch?.[key] ?? '';
		}
		this.filterValues.set({
			...restoredFilterValues,
			advisor: (parsed.filterValues?.advisor ?? []).filter(isNumericFilterId),
		});
		this.filterValues.update((state) => ({
			...state,
			phase: [...new Set(state.phase.map((value) => (value === 'P0' ? 'P1' : value)))],
		}));
		this.filterSearch.set({
			...restoredFilterSearch,
			advisor: '',
		});
		if (parsed.selectedColumns) this.selectedColumnIds.set(parsed.selectedColumns);
		if (parsed.dateRanges) this.dateRanges.set(this.parseStoredDateRanges(parsed.dateRanges, parsed.selectedColumns));
		const restoredReferenceDate = parseStoredDay(parsed.referenceDate ?? null);
		if (restoredReferenceDate && restoredReferenceDate <= new Date()) this.referenceDate.set(restoredReferenceDate);
		// Without stored columns the defaults apply later; the schema load syncs then.
		if (parsed.selectedColumns) this.syncReferenceDate(parsed.selectedColumns);
	}

	/** Clears the reference date when no atDate column is selected, so no hidden value survives. */
	private syncReferenceDate(selectedIds: readonly string[]) {
		if (!isReferenceDateAvailable(selectedIds) && this.referenceDate()) this.referenceDate.set(null);
	}

	public setReferenceDate(value: Date | null) {
		this.referenceDate.set(value);
		this.pageIndex.set(0);
		this.persistStateToStorage();
		void this.loadAssignments();
	}

	public isReferenceDateColumn(column: AssignmentColumn): boolean {
		const schemaId: string = ASSIGNMENT_SCHEMA_IDS[column.key];
		return (REFERENCE_DATE_COLUMN_IDS as readonly string[]).includes(schemaId);
	}

	private parseStoredDateRanges(
		stored: NonNullable<AssignmentStoredState['dateRanges']>,
		selectedColumns?: string[],
	): Partial<Record<AssignmentDateRangeFieldId, DatabaseDateRangeValue>> {
		const restored: Partial<Record<AssignmentDateRangeFieldId, DatabaseDateRangeValue>> = {};
		for (const fieldId of ASSIGNMENT_DATE_RANGE_FIELD_IDS) {
			if (selectedColumns && !selectedColumns.includes(fieldId)) continue;
			const entry = stored[fieldId];
			const start = parseStoredDay(entry?.start);
			const end = parseStoredDay(entry?.end);
			if (!start && !end) continue;
			if (start && end && start > end) continue;
			restored[fieldId] = { start, end };
		}
		return restored;
	}

	private queryDateRanges(
		ranges: Partial<Record<string, DatabaseDateRangeValue>> = this.dateRanges(),
		selectedIds: readonly string[] = this.selectedColumnIds(),
	): Partial<Record<string, { start: string; end: string }>> {
		const query: Partial<Record<string, { start: string; end: string }>> = {};
		for (const [fieldId, value] of Object.entries(ranges)) {
			if (!value || !selectedIds.includes(fieldId)) continue;
			query[fieldId] = {
				start: formatDateToISOString(value.start) || '',
				end: formatDateToISOString(value.end) || '',
			};
		}
		return query;
	}

	private storedDateRanges(): NonNullable<AssignmentStoredState['dateRanges']> {
		const stored: NonNullable<AssignmentStoredState['dateRanges']> = {};
		for (const [fieldId, value] of Object.entries(this.dateRanges())) {
			if (!value) continue;
			stored[fieldId] = {
				start: formatDateToISOString(value.start) || null,
				end: formatDateToISOString(value.end) || null,
			};
		}
		return stored;
	}

	private persistStateToStorage() {
		const state = {
			pageIndex: this.pageIndex(),
			sortField: this.sortField(),
			sortDirection: this.sortDirection(),
			rangeStart: formatDateToISOString(this.rangeStart()) ?? null,
			rangeEnd: formatDateToISOString(this.rangeEnd()) ?? null,
			rangePreset: this.rangePreset(),
			filterValues: this.filterValues(),
			filterSearch: this.filterSearch(),
			selectedColumns: this.selectedColumnIds(),
			dateRanges: this.storedDateRanges(),
			referenceDate: this.storedReferenceDate(),
		};
		this.viewStorage.writeListState(TAB_ID, state);
	}

	private storedReferenceDate(): string | null {
		const value = this.referenceDate();
		return value ? (formatDateToISOString(new Date(value)) ?? null) : null;
	}

	private getCurrentUserId(): string | undefined {
		return this.storage?.resolveCurrentUserId(this.auth);
	}

	private getSavedPageSize(): number | undefined {
		const userId = this.getCurrentUserId();
		if (!userId || !this.storage) return undefined;

		const saved = this.storage.getPageSize(userId, PAGINATOR_PREFERENCE_KEY);
		if (!saved || !this.environment.pageSizeOptions.includes(saved)) return undefined;
		return saved;
	}
}
